import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ExternalLink, MoreHorizontal, Copy, Clipboard, Eye } from "lucide-react";
import { Mask } from "@/utils/mask";
import { getShowContests } from "@/Api/contests";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SharingBtn } from "@/components/Sharing";
import { CreateLoaderInput, LoaderSpin } from "@/components/Loaders";
import { TypeTableProps } from "@/App/Plans/Table";
import { TableDataProps } from "@/pages/Contests";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToastNavigate } from "@/utils/toastNavigate";
import { Check, Download, Upload, FileSpreadsheet } from "lucide-react";
import {
  storeDistributionsDataUnityBalancesWithFiles,
  getDistributionSimplified,
  getFullDistributionPath,
  exportDistributionTemplate,
  importDistributionPreview,
  confirmImportDistribution,
} from "@/Api/distributions";
import { FileUpload } from "@/App/Distributions/components/FileUpload";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { If } from "@/components/If";
import { usePermissions } from "@/hooks/usePermissions";

// Interfaces removidas - não são mais necessárias com o novo endpoint

// Interfaces para detalhes de distribuição
interface DistributionFile {
  id: number;
  name: string | null;
  file_name: string;
  file_url: string;
}

interface DistributionOriginDestination {
  type: 'admin' | 'unity' | 'seller';
  id?: number;
  name: string;
}

interface DistributionDetail {
  distribution_id: number;
  sale_id?: number | null;
  origin: DistributionOriginDestination | null;
  destination: DistributionOriginDestination | null;
  unity_id: number;
  unity_name: string;
  from_unity_id?: number;
  from_unity_name?: string;
  seller_id?: number;
  seller_name?: string;
  from_seller_id?: number;
  from_seller_name?: string;
  created_by: {
    id: number;
    name: string;
  };
  updated_by: {
    id: number;
    name: string;
  };
  distribution_type: {
    id: number;
    name: string;
  };
  status: {
    id: number;
    name: string;
  };
  contest_product_balance: {
    current_balance: string;
    held_balance: string;
  };
  quota_quantity: number;
  created_at: string;
  processed_at?: string;
  files?: DistributionFile[];
}

interface DistributionDetailsResponse {
  contest_product_classification: {
    id: number;
    product_name: string;
    sale_price: number;
    quota_total_quantity: number;
    quota_total_distributed: number;
  };
  distributions: DistributionDetail[];
}

// Nova estrutura simplificada do backend
interface SimplifiedProductBalance {
  current_balance: number;
  held_balance: number;
  total_distributed: string;
  consumed_quantity: string;
}

interface SimplifiedUnity {
  id: number;
  name: string;
  contest_product_balance: SimplifiedProductBalance;
}

interface SimplifiedProduct {
  contest_product_classification: {
    id: number;
    product_name: string;
    sale_price: number;
    quota_total_quantity: number;
    quota_total_distributed: number;
  };
  unities: SimplifiedUnity[];
}

// Interface SimplifiedResponse removida - não é necessária

interface Product {
  id: number;
  product_name: string;
  product_classification: {
    quota_quantity: number;
    sale_price: number;
  };
  lottery_id: number;
}

const schema = z.object({
  contest_id: z.number(),
  lottery_id: z.number(),
  contest_product_classification_id: z.number(),
  unities: z.array(
    z.object({
      id: z.number(),
      quota_quantity: z.number().min(0, "Quantidade mínima é 0"),
    })
  ),
});

type SchemaType = z.infer<typeof schema>;

const DistributionDetailsModal = ({
  isOpen,
  onClose,
  contestId,
  productId,
  productName,
}: {
  isOpen: boolean;
  onClose: () => void;
  contestId: string;
  productId: number;
  productName: string;
}) => {
  const [details, setDetails] = useState<DistributionDetailsResponse | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<number | null>(null);
  const { toast } = useToastNavigate();

  useEffect(() => {
    if (isOpen && contestId && productId) {
      fetchDetails();
    }
  }, [isOpen, contestId, productId]);

  const fetchDetails = async () => {
    setLoading(true);
    try {
      const response = await getFullDistributionPath(contestId, productId);
      if (response.data?.status) {
        setDetails(response.data.response);
      }
    } catch (error) {
      console.error("Erro ao buscar detalhes:", error);
      toast({
        variant: "destructive",
        title: "Erro ao carregar detalhes",
        description: "Não foi possível carregar os detalhes das distribuições.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Verifica se é aceito automaticamente (Admin -> LOTERIAS ALDEOTA com qty 1)
  const isAutoAccepted = (dist: DistributionDetail) => {
    return (
      dist.origin?.type === 'admin' &&
      dist.destination?.name?.toUpperCase().includes('LOTERIAS ALDEOTA') &&
      dist.quota_quantity === 1 &&
      dist.status.id === 2
    );
  };

  const getStatusColor = (statusId: number, distributionTypeId?: number, dist?: DistributionDetail) => {
    // Aceito automaticamente (Admin -> LOTERIAS ALDEOTA com qty 1)
    if (dist && isAutoAccepted(dist)) {
      return "bg-emerald-500 text-white font-semibold whitespace-nowrap";
    }
    // Pré-venda aceita automaticamente tem cor diferente
    if (distributionTypeId === 7 && statusId === 2) {
      return "bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200";
    }
    switch (statusId) {
      case 1:
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
      case 2:
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case 3:
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200";
    }
  };

  const getStatusLabel = (statusName: string, distributionTypeId?: number, dist?: DistributionDetail) => {
    // Aceito automaticamente (Admin -> LOTERIAS ALDEOTA com qty 1)
    if (dist && isAutoAccepted(dist)) {
      return "ACEITO AUTO";
    }
    // Pré-venda aceita mostra "Aceito Automaticamente"
    if (distributionTypeId === 7 && statusName.toLowerCase().includes("aceito")) {
      return "Aceito Automaticamente";
    }
    return statusName;
  };

  const getDistributionTypeColor = (typeId: number) => {
    switch (typeId) {
      case 1: // Admin -> Unidade
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
      case 2: // Unidade -> Vendedor
        return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200";
      case 3: // Unidade -> Unidade
        return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200";
      case 4: // Vendedor -> Vendedor
        return "bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200";
      case 5: // Vendedor -> Unidade
        return "bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200";
      case 7: // Pré-venda
        return "bg-yellow-400 text-yellow-900 dark:bg-yellow-500 dark:text-yellow-900 font-semibold";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200";
    }
  };

  const getOriginDestinationLabel = (dist: DistributionDetail) => {
    if (!dist.origin || !dist.destination) return null;

    return (
      <div className="flex items-center gap-2 text-sm">
        <span className={cn(
          "px-2 py-0.5 rounded text-xs font-medium",
          dist.origin.type === 'admin' ? "bg-slate-200 dark:bg-slate-700" :
          dist.origin.type === 'unity' ? "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200" :
          "bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200"
        )}>
          {dist.origin.name}
        </span>
        <span className="text-gray-400">→</span>
        <span className={cn(
          "px-2 py-0.5 rounded text-xs font-medium",
          dist.destination.type === 'unity' ? "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200" :
          "bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200"
        )}>
          {dist.destination.name}
        </span>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="pb-0">
          <DialogTitle className="text-xl">Histórico de Distribuição</DialogTitle>
          <DialogDescription className="sr-only">{productName}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <LoaderSpin />
            </div>
          ) : details ? (
            <div className="space-y-4">
              {/* Header melhorado */}
              <div className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                      {productName}
                    </h3>
                  </div>
                  <div className="flex gap-6">
                    <div className="text-center px-4 py-2 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-600 shadow-sm">
                      <p className="text-2xl font-bold text-slate-900 dark:text-white">
                        {details.contest_product_classification.quota_total_quantity}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                        Total
                      </p>
                    </div>
                    <div className="text-center px-4 py-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-700 shadow-sm">
                      <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {details.contest_product_classification.quota_total_distributed}
                      </p>
                      <p className="text-xs text-blue-600 dark:text-blue-400 uppercase tracking-wide">
                        Distribuídas
                      </p>
                    </div>
                    <div className="text-center px-4 py-2 bg-amber-50 dark:bg-amber-900/30 rounded-lg border border-amber-200 dark:border-amber-700 shadow-sm">
                      <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                        {details.contest_product_classification.quota_total_quantity - details.contest_product_classification.quota_total_distributed}
                      </p>
                      <p className="text-xs text-amber-600 dark:text-amber-400 uppercase tracking-wide">
                        Restantes
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Filtros dos tipos de distribuição */}
              <div className="flex flex-wrap items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded">
                <span className="text-xs text-gray-500 dark:text-gray-400 mr-2">Filtrar por tipo:</span>
                <button
                  onClick={() => setSelectedTypeFilter(null)}
                  className={cn(
                    "px-2 py-1 rounded text-xs font-medium transition-all cursor-pointer border",
                    selectedTypeFilter === null
                      ? "bg-slate-700 text-white border-slate-700 dark:bg-slate-200 dark:text-slate-900"
                      : "bg-slate-100 text-slate-600 border-slate-300 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600"
                  )}
                >
                  Todos ({details.distributions.length})
                </button>
                {[
                  { id: 1, label: "Admin → Unidade", alwaysShow: false },
                  { id: 2, label: "Unidade → Vendedor", alwaysShow: false },
                  { id: 3, label: "Unidade → Unidade", alwaysShow: false },
                  { id: 4, label: "Vendedor → Vendedor", alwaysShow: false },
                  { id: 5, label: "Vendedor → Unidade", alwaysShow: false },
                  { id: 7, label: "Pré-venda", alwaysShow: true },
                ].map((type) => {
                  const count = details.distributions.filter(d => d.distribution_type.id === type.id).length;
                  if (count === 0 && !type.alwaysShow) return null;
                  return (
                    <button
                      key={type.id}
                      onClick={() => setSelectedTypeFilter(selectedTypeFilter === type.id ? null : type.id)}
                      className={cn(
                        "px-2 py-1 rounded text-xs font-medium transition-all cursor-pointer border",
                        selectedTypeFilter === type.id
                          ? "ring-2 ring-offset-1 ring-slate-500"
                          : "hover:opacity-80",
                        getDistributionTypeColor(type.id)
                      )}
                    >
                      {type.label} ({count})
                    </button>
                  );
                })}
              </div>

              <div className="space-y-2">
                {(() => {
                  const filteredDistributions = selectedTypeFilter
                    ? details.distributions.filter(d => d.distribution_type.id === selectedTypeFilter)
                    : details.distributions;

                  return (
                    <>
                      <h3 className="font-semibold text-lg">
                        Caminho das Distribuições ({filteredDistributions.length} de {details.distributions.length} registros)
                      </h3>
                      {filteredDistributions.length > 0 ? (
                        <div className="border rounded-lg overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>De → Para</TableHead>
                                <TableHead>Tipo</TableHead>
                                <TableHead>Qtd</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Criado por</TableHead>
                                <TableHead>Data</TableHead>
                                <TableHead>ID Venda</TableHead>
                                <TableHead className="text-center">Arquivos</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {filteredDistributions.map((dist) => (
                                <TableRow
                                  key={dist.distribution_id}
                                  className={cn(
                                    dist.distribution_type.id === 7 && "bg-yellow-100 dark:bg-yellow-900/30 border-l-4 border-l-yellow-500",
                                    isAutoAccepted(dist) && "bg-yellow-50 dark:bg-yellow-900/20"
                                  )}
                                >
                                  <TableCell className="min-w-[200px]">
                                    {getOriginDestinationLabel(dist)}
                                  </TableCell>
                                  <TableCell>
                                    <span
                                      className={cn(
                                        "px-2 py-1 rounded text-xs font-medium whitespace-nowrap",
                                        getDistributionTypeColor(dist.distribution_type.id)
                                      )}
                                    >
                                      {dist.distribution_type.name}
                                    </span>
                                  </TableCell>
                                  <TableCell className="font-medium">
                                    {dist.quota_quantity}
                                  </TableCell>
                                  <TableCell>
                                    <span
                                      className={cn(
                                        "px-2 py-1 rounded-full text-xs font-medium",
                                        getStatusColor(dist.status.id, dist.distribution_type.id, dist)
                                      )}
                                    >
                                      {getStatusLabel(dist.status.name, dist.distribution_type.id, dist)}
                                    </span>
                                  </TableCell>
                                  <TableCell className="text-sm">
                                    {dist.created_by.name}
                                  </TableCell>
                                  <TableCell className="text-sm whitespace-nowrap">
                                    {new Date(dist.created_at).toLocaleString("pt-BR", {
                                      day: "2-digit",
                                      month: "2-digit",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}
                                  </TableCell>
                                  <TableCell>
                                    {dist.sale_id ? (
                                      <Link
                                        to={`/vendas?sale_id=${dist.sale_id}`}
                                        className="inline-flex items-center gap-1 text-blue-600 hover:underline text-sm font-medium whitespace-nowrap"
                                        title="Ir para a venda"
                                      >
                                        #{dist.sale_id}
                                        <ExternalLink className="h-3 w-3" />
                                      </Link>
                                    ) : (
                                      <span className="text-gray-400 text-xs">-</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-center">
                                    {dist.files && dist.files.length > 0 ? (
                                      dist.files.length === 1 ? (
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                                          onClick={() => window.open(dist.files![0].file_url, "_blank")}
                                          title={dist.files[0].file_name || "Baixar arquivo"}
                                        >
                                          <Download className="h-4 w-4" />
                                        </Button>
                                      ) : (
                                        <DropdownMenu>
                                          <DropdownMenuTrigger asChild>
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                                              title={`${dist.files.length} arquivos`}
                                            >
                                              <Download className="h-4 w-4" />
                                            </Button>
                                          </DropdownMenuTrigger>
                                          <DropdownMenuContent align="end">
                                            {dist.files.map((file) => (
                                              <DropdownMenuItem
                                                key={file.id}
                                                onClick={() => window.open(file.file_url, "_blank")}
                                                className="cursor-pointer"
                                              >
                                                <Download className="h-4 w-4 mr-2" />
                                                {file.file_name || `Arquivo ${file.id}`}
                                              </DropdownMenuItem>
                                            ))}
                                          </DropdownMenuContent>
                                        </DropdownMenu>
                                      )
                                    ) : (
                                      <span className="text-gray-400 text-xs">-</span>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          {selectedTypeFilter
                            ? "Nenhuma distribuição deste tipo encontrada."
                            : "Nenhuma distribuição registrada para este produto."}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Nenhum detalhe disponível.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const TableData = ({
  dataApi,
  pageActions,
}: TypeTableProps<TableDataProps>) => {
  const [searchParams] = useSearchParams();
  const contestId = searchParams.get("q");
  const showBtnRefs = useRef<{ [key: string]: HTMLElement }>({});
  const { hasPermission } = usePermissions();
  useEffect(() => {
    if (contestId) {
      const decodedId = atob(contestId);
      const contest = dataApi?.data.find(
        (item) => item.id === Number(decodedId)
      );
      if (contest && showBtnRefs.current[contest.uuid]) {
        const button =
          showBtnRefs.current[contest.uuid].querySelector("button");
        if (button) {
          button.click();
        }
      }
    }
  }, [contestId, dataApi?.data]);

  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>Loteria</TableHead>
            <TableHead>Data de validade</TableHead>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          {dataApi?.data.map((item) => (
            <TableRow key={item?.id}>
              <TableCell className="truncate max-w-80">{item?.name}</TableCell>
              <TableCell>
                <span
                  className="px-2 py-1 rounded text-white text-sm font-medium"
                  style={{ backgroundColor: item?.lottery?.color }}
                >
                  {item?.lottery?.name}
                </span>
              </TableCell>
              <TableCell>
                {item?.validation_date
                  ? Mask.transformMaskBirthdayInBr(item?.validation_date)
                  : "Não informado"}
              </TableCell>
              <TableCell className="flex justify-center">
                <div className="flex gap-3 w-full justify-center">
                  <div
                    ref={(el) => el && (showBtnRefs.current[item.uuid] = el)}
                  >
                    <If condition={hasPermission("create-distributions")}>
                      <SharingBtn
                        labels={{ title: "Visualizar Distribuições" }}
                        params={{
                          func: getShowContests,
                          funcParams: item?.uuid,
                        }}
                        className="sm:max-w-[700px]"
                        render={(responseApi: TableDataProps) => (
                          <InsideContestModal contestData={responseApi} />
                        )}
                        loader={<CreateLoaderInput lines={4} />}
                      />
                    </If>
                  </div>
                </div>
              </TableCell>
            </TableRow>
          ))}

          {dataApi?.data.length < 1 && (
            <TableRow>
              <TableCell colSpan={5}>Nada foi encontrado.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <div className="flex justify-between mb-3">
        {dataApi?.data.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </>
  );
};

interface PaginationInfo {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  from: number | null;
  to: number | null;
}

function InsideContestModal({ contestData }: { contestData: TableDataProps }) {
  const { toast } = useToastNavigate();
  const [simplifiedData, setSimplifiedData] = useState<SimplifiedProduct[]>([]);
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [productFilter, setProductFilter] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [distributingProductId, setDistributingProductId] = useState<
    number | null
  >(null);
  const [quotaStates, setQuotaStates] = useState<{
    [key: string]: { unityId: number; quota: number }[];
  }>({});
  const [originalQuotaStates, setOriginalQuotaStates] = useState<{
    [key: string]: { unityId: number; quota: number }[];
  }>({});
  // Ref para armazenar as alterações do usuário (não é resetado pelo fetchPage)
  const userChangesRef = useRef<{
    [productId: string]: { unityId: number; quota: number }[];
  }>({});
  const [confirmDialogProduct, setConfirmDialogProduct] = useState<any>(null);
  const [showDistributeAllDialog, setShowDistributeAllDialog] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedFilesAll, setSelectedFilesAll] = useState<File[]>([]);
  const [isDistributingAll, setIsDistributingAll] = useState(false);
  // Snapshot dos dados a distribuir quando o dialog é aberto
  const [distributeAllSnapshot, setDistributeAllSnapshot] = useState<{
    [productId: string]: { unityId: number; quota: number }[];
  } | null>(null);
  const [copiedDistribution, setCopiedDistribution] = useState<
    | {
        unityId: number;
        quota: number;
      }[]
    | null
  >(null);
  // Novos states para o modal de detalhes
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedProductDetails, setSelectedProductDetails] = useState<{
    id: number;
    name: string;
  } | null>(null);
  // Cache de nomes de produtos e unidades para exibir no dialog de distribuir tudo
  const [productNamesCache, setProductNamesCache] = useState<{
    [productId: string]: {
      productName: string;
      unities: { [unityId: string]: string };
    };
  }>({});

  // Estados para import/export
  const [isExporting, setIsExporting] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<any>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isConfirmingImport, setIsConfirmingImport] = useState(false);
  const [importConfirmFiles, setImportConfirmFiles] = useState<File[]>([]);

  const fetchPage = async (page: number, filter?: string) => {
    setIsLoading(true);
    try {
      const searchFilter = filter !== undefined ? filter : productFilter;
      const productName = searchFilter ? `B${searchFilter}` : undefined;
      const response = await getDistributionSimplified(
        String(contestData.id),
        page,
        100,
        productName
      );

      if (response.data?.status) {
        const responseData = response.data.response;
        const data = responseData.data as SimplifiedProduct[];
        setSimplifiedData(data);
        setPaginationInfo({
          current_page: responseData.current_page,
          per_page: responseData.per_page,
          total: responseData.total,
          last_page: responseData.last_page,
          from: responseData.from,
          to: responseData.to,
        });

        // Acumular quotaStates - manter alterações de outras páginas
        setQuotaStates((prev) => {
          const newStates = { ...prev };
          data.forEach((product) => {
            const productId = product.contest_product_classification.id;
            // Só inicializa se não existir ainda (mantém alterações feitas pelo usuário)
            if (!newStates[productId]) {
              newStates[productId] = product.unities.map((unity) => ({
                unityId: unity.id,
                quota: 0,
              }));
            }
          });
          return newStates;
        });

        // Acumular originalQuotaStates - sempre atualiza com dados mais recentes do backend
        setOriginalQuotaStates((prev) => {
          const newStates = { ...prev };
          data.forEach((product) => {
            const productId = product.contest_product_classification.id;
            newStates[productId] = product.unities.map((unity) => ({
              unityId: unity.id,
              quota:
                parseInt(unity.contest_product_balance.total_distributed) || 0,
            }));
          });
          return newStates;
        });

        // Cache de nomes de produtos e unidades para exibir no dialog
        setProductNamesCache((prev) => {
          const newCache = { ...prev };
          data.forEach((product) => {
            const productId = product.contest_product_classification.id;
            const unitiesMap: { [unityId: string]: string } = {};
            product.unities.forEach((unity) => {
              unitiesMap[unity.id] = unity.name;
            });
            newCache[productId] = {
              productName: product.contest_product_classification.product_name,
              unities: unitiesMap,
            };
          });
          return newCache;
        });
      }
    } catch (error) {
      console.error("Erro ao buscar dados simplificados:", error);
      toast({
        variant: "destructive",
        title: "Erro ao carregar dados",
        description: "Não foi possível carregar as distribuições.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPage(currentPage);
  }, [contestData.id, currentPage]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= (paginationInfo?.last_page || 1)) {
      setCurrentPage(page);
    }
  };

  const handleFilterChange = (value: string) => {
    const numericValue = value.replace(/\D/g, "");
    setProductFilter(numericValue);
  };

  const handleFilterSearch = () => {
    setCurrentPage(1);
    fetchPage(1, productFilter);
  };

  const handleFilterKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleFilterSearch();
    }
  };

  const handleClearFilter = () => {
    setProductFilter("");
    setCurrentPage(1);
    fetchPage(1, "");
  };

  const handleOpenDistributeAllDialog = () => {
    const snapshot: { [productId: string]: { unityId: number; quota: number }[] } = {};

    // 1. Primeiro, pega do userChangesRef (alterações de páginas anteriores)
    Object.entries(userChangesRef.current).forEach(([productId, quotas]) => {
      const quotasWithValues = quotas.filter(q => q.quota > 0);
      if (quotasWithValues.length > 0) {
        snapshot[productId] = quotasWithValues.map(q => ({ ...q }));
      }
    });

    // 2. Depois, pega do quotaStates atual (página atual) - sobrescreve se já existir
    Object.entries(quotaStates).forEach(([productId, quotas]) => {
      const quotasWithValues = quotas.filter(q => q.quota > 0);
      if (quotasWithValues.length > 0) {
        snapshot[productId] = quotasWithValues.map(q => ({ ...q }));
      }
    });

    setDistributeAllSnapshot(snapshot);
    setShowDistributeAllDialog(true);
  };

  function handleShowDetails(productId: number, productName: string) {
    setSelectedProductDetails({ id: productId, name: productName });
    setShowDetailsModal(true);
  }

  // Export template handler
  const handleExportTemplate = async () => {
    setIsExporting(true);
    try {
      const blob = await exportDistributionTemplate(String(contestData.id));
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `distribuicao_${contestData?.lottery?.name}_${contestData?.name}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast({
        description: "Modelo exportado com sucesso!",
        action: <Check color="#1edc2b" />,
      });
    } catch (error) {
      console.error("Erro ao exportar modelo:", error);
      toast({
        variant: "destructive",
        title: "Erro ao exportar",
        description: "Não foi possível exportar o modelo de distribuição.",
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Import preview handler
  const handleImportPreview = async () => {
    if (!importFile) {
      toast({
        variant: "destructive",
        description: "Selecione um arquivo Excel para importar.",
      });
      return;
    }

    setIsImporting(true);
    try {
      const response = await importDistributionPreview(String(contestData.id), importFile);
      if (response.data?.status) {
        setImportPreview(response.data.response);
      } else {
        throw new Error(response.data?.message || "Erro ao processar arquivo");
      }
    } catch (error: any) {
      console.error("Erro ao importar:", error);
      toast({
        variant: "destructive",
        title: "Erro ao importar",
        description: error?.response?.data?.message || "Não foi possível processar o arquivo.",
      });
    } finally {
      setIsImporting(false);
    }
  };

  // Confirm import handler
  const handleConfirmImport = async () => {
    if (!importPreview?.distributions?.length) {
      toast({
        variant: "destructive",
        description: "Nenhuma distribuição para confirmar.",
      });
      return;
    }

    if (importConfirmFiles.length === 0) {
      toast({
        variant: "destructive",
        description: "Selecione pelo menos um arquivo de comprovação.",
      });
      return;
    }

    setIsConfirmingImport(true);
    try {
      const response = await confirmImportDistribution(
        String(contestData.id),
        importPreview.distributions,
        importConfirmFiles
      );

      if (response.data?.status) {
        toast({
          description: "Distribuição importada com sucesso!",
          action: <Check color="#1edc2b" />,
        });
        setShowImportDialog(false);
        setImportFile(null);
        setImportPreview(null);
        setImportConfirmFiles([]);
        await fetchPage(currentPage);
      } else {
        throw new Error(response.data?.message || "Erro ao confirmar importação");
      }
    } catch (error: any) {
      console.error("Erro ao confirmar importação:", error);
      toast({
        variant: "destructive",
        title: "Erro ao confirmar",
        description: error?.response?.data?.message || "Não foi possível confirmar a distribuição.",
      });
    } finally {
      setIsConfirmingImport(false);
    }
  };

  // Reset import dialog
  const handleCloseImportDialog = () => {
    setShowImportDialog(false);
    setImportFile(null);
    setImportPreview(null);
    setImportConfirmFiles([]);
  };

  function getDistributedQuota(productId: number, unityId: number): number {
    const product = simplifiedData.find(
      (p) => p.contest_product_classification.id === productId
    );
    const unity = product?.unities.find((u) => u.id === unityId);
    return parseInt(unity?.contest_product_balance.total_distributed || "0");
  }

  function getQuotaValidationInfo(
    productId: number,
    currentInputs: { unityId: number; quota: number }[]
  ) {
    const totalInputQuotas = currentInputs.reduce(
      (sum, item) => sum + item.quota,
      0
    );
    const availableBalance = getAvailableBalance(productId);
    const withHeldBalance = getWithHeldBalance(productId);

    const isOverLimit = totalInputQuotas > availableBalance;
    const remaining = availableBalance - totalInputQuotas;

    return {
      totalInputQuotas,
      availableBalance,
      withHeldBalance,
      isOverLimit,
      remaining,
      canProceed: !isOverLimit,
    };
  }

  function getAvailableBalance(productId: number): number {
    const product = simplifiedData.find(
      (p) => p.contest_product_classification.id === productId
    );
    if (!product) return 0;

    // Saldo disponível é a diferença entre total e distribuído
    const totalQuotas =
      product.contest_product_classification.quota_total_quantity;
    const totalDistributed =
      product.contest_product_classification.quota_total_distributed;
    // Nunca negativo: inconsistência de dados não deve travar/confundir a distribuição
    return Math.max(0, totalQuotas - totalDistributed);
  }

  function getWithHeldBalance(productId: number): number {
    const product = simplifiedData.find(
      (p) => p.contest_product_classification.id === productId
    );
    if (!product) return 0;

    // Somar todos os held_balance das unidades
    return product.unities.reduce((sum, unity) => {
      return sum + unity.contest_product_balance.held_balance;
    }, 0);
  }

  // function getManualDistributions(productId: number): number {
  //   return distributions.data
  //     .filter(
  //       (dist) =>
  //         dist.cp_classification_id === productId &&
  //         dist.distribution_process_id !== null
  //     )
  //     .reduce((sum, dist) => sum + dist.quota_quantity, 0);
  // }

  // function getAutomaticDistributions(productId: number): number {
  //   return distributions.data
  //     .filter(
  //       (dist) =>
  //         dist.cp_classification_id === productId &&
  //         dist.distribution_process_id === null
  //     )
  //     .reduce((sum, dist) => sum + dist.quota_quantity, 0);
  // }

  // Este useEffect agora é desnecessário porque os dados já são inicializados no fetchAll
  // Os quotaStates são inicializados com os valores do backend

  function handleQuotaChange(
    productId: number,
    unityId: number,
    quota: number
  ) {

    setQuotaStates((prev) => {
      const newStates = {
        ...prev,
        [productId]: prev[productId]?.map((item) =>
          item.unityId === unityId ? { ...item, quota } : item
        ) || [{ unityId, quota }],
      };
      return newStates;
    });

    // Também salva no ref para garantir persistência
    if (!userChangesRef.current[productId]) {
      userChangesRef.current[productId] = [];
    }
    const existingIndex = userChangesRef.current[productId].findIndex(
      (item) => item.unityId === unityId
    );
    if (existingIndex >= 0) {
      userChangesRef.current[productId][existingIndex].quota = quota;
    } else {
      userChangesRef.current[productId].push({ unityId, quota });
    }

  }

  function handleCopyDistribution(productId: number) {
    const product = simplifiedData.find(
      (p) => p.contest_product_classification.id === productId
    );
    if (!product) return;

    const distributionToCopy = product.unities.map((unity) => ({
      unityId: unity.id,
      quota: unity.name.toLowerCase().includes("loterias aldeota")
        ? 0
        : parseInt(unity.contest_product_balance.total_distributed || "0"),
    }));
    setCopiedDistribution(distributionToCopy);
    toast({
      description: "Distribuição copiada com sucesso!",
      action: <Copy className="w-4 h-4" />,
    });
  }

  function handleCopyCurrentDistribution(productId: number) {
    const currentQuotas = quotaStates[productId] || [];
    const nonZeroQuotas = currentQuotas.filter((quota) => quota.quota > 0);

    if (nonZeroQuotas.length === 0) {
      toast({
        variant: "destructive",
        description: "Nenhuma cota foi definida para copiar.",
      });
      return;
    }

    // Copia todos os valores não-zero
    setCopiedDistribution(
      nonZeroQuotas.map((quota) => ({
        unityId: quota.unityId,
        quota: quota.quota,
      }))
    );

    const totalQuotas = nonZeroQuotas.reduce(
      (sum, quota) => sum + quota.quota,
      0
    );
    toast({
      description: `Distribuição atual copiada: ${totalQuotas} cotas em ${nonZeroQuotas.length} unidade(s)`,
      action: <Copy className="w-4 h-4" />,
    });
  }

  function handlePasteDistribution(productId: number) {
    if (!copiedDistribution || copiedDistribution.length === 0) {
      toast({
        variant: "destructive",
        description: "Nenhuma distribuição copiada encontrada.",
      });
      return;
    }

    setQuotaStates((prev) => ({
      ...prev,
      [productId]: prev[productId].map((item) => {
        const match = copiedDistribution.find(
          (q) => q.unityId === item.unityId
        );
        return match ? { ...item, quota: match.quota } : item;
      }),
    }));

    // Também salva no ref para garantir persistência
    if (!userChangesRef.current[productId]) {
      userChangesRef.current[productId] = [];
    }
    copiedDistribution.forEach((copied) => {
      const existingIndex = userChangesRef.current[productId].findIndex(
        (item) => item.unityId === copied.unityId
      );
      if (existingIndex >= 0) {
        userChangesRef.current[productId][existingIndex].quota = copied.quota;
      } else {
        userChangesRef.current[productId].push({ unityId: copied.unityId, quota: copied.quota });
      }
    });

    toast({
      description: "Distribuição colada com sucesso!",
      action: <Clipboard className="w-4 h-4" />,
    });
  }

  async function handleSubmitDistribution(product: Product) {
    if (selectedFiles.length === 0) {
      toast({
        variant: "destructive",
        description: "Selecione pelo menos um arquivo para confirmar a distribuição.",
      });
      return;
    }

    setDistributingProductId(product.id);
    const localQuotas = quotaStates[product.id] || [];
    const originalQuotas = originalQuotaStates[product.id] || [];

    const modifiedUnities = localQuotas.filter((currentQuota) => {
      const originalQuota = originalQuotas.find(
        (oq) => oq.unityId === currentQuota.unityId
      );
      // Só incluir se realmente mudou E se o valor atual não for 0
      return originalQuota && originalQuota.quota !== currentQuota.quota && currentQuota.quota > 0;
    });

    if (modifiedUnities.length === 0) {
      toast({
        description: "Nenhuma alteração foi detectada para enviar.",
      });
      setDistributingProductId(null);
      return;
    }

    const dataToSubmit: SchemaType = {
      contest_id: contestData.id,
      lottery_id: product.lottery_id,
      contest_product_classification_id: product.id,
      unities: modifiedUnities.map((uq) => ({
        id: uq.unityId,
        quota_quantity: uq.quota,
      })),
    };

    const parseResult = schema.safeParse(dataToSubmit);
    if (!parseResult.success) {
      toast({
        variant: "destructive",
        description: "Verifique os campos de distribuição.",
      });
      setDistributingProductId(null);
      return;
    }

    const validationInfo = getQuotaValidationInfo(product.id, localQuotas);

    if (validationInfo.isOverLimit) {
      const excess =
        validationInfo.totalInputQuotas - validationInfo.availableBalance;
      toast({
        variant: "destructive",
        title: "Distribuição bloqueada",
        description: `Não é possível prosseguir. Você está tentando distribuir ${
          validationInfo.totalInputQuotas
        } cotas, mas há apenas ${
          validationInfo.availableBalance
        } disponíveis. Excesso: ${excess} cotas.${
          validationInfo.withHeldBalance > 0
            ? ` (${validationInfo.withHeldBalance} em processamento)`
            : ""
        }`,
      });
      setDistributingProductId(null);
      return;
    }

    const maxProductQuotas = product.product_classification.quota_quantity;
    if (validationInfo.totalInputQuotas > maxProductQuotas) {
      toast({
        variant: "destructive",
        title: "Limite do produto excedido",
        description: `O total de cotas (${validationInfo.totalInputQuotas}) excede o limite máximo do produto (${maxProductQuotas}).`,
      });
      setDistributingProductId(null);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("contest_id", dataToSubmit.contest_id.toString());
      formData.append("lottery_id", dataToSubmit.lottery_id.toString());
      formData.append("contest_product_classification_id", dataToSubmit.contest_product_classification_id.toString());

      dataToSubmit.unities.forEach((unity, index) => {
        formData.append(`unities[${index}][id]`, unity.id.toString());
        formData.append(`unities[${index}][quota_quantity]`, unity.quota_quantity.toString());
      });

      selectedFiles.forEach((file) => {
        formData.append("files[]", file);
      });

      const { ok } = await storeDistributionsDataUnityBalancesWithFiles(
        String(contestData.id),
        formData
      );
      if (!ok) {
        throw new Error("Erro ao registrar distribuição");
      }
      setSelectedFiles([]);
      toast({
        description: `Distribuição salva com sucesso (${
          modifiedUnities.length
        } unidade${modifiedUnities.length > 1 ? "s" : ""} atualizada${
          modifiedUnities.length > 1 ? "s" : ""
        })`,
        action: <Check color="#1edc2b" />,
      });

      // Recarregar dados simplificados após salvar usando a página atual
      await fetchPage(currentPage);
    } catch (error) {
      toast({
        variant: "destructive",
        description:
          error instanceof Error ? error.message : "Erro ao criar distribuição",
      });
    } finally {
      setDistributingProductId(null);
    }
  }

  async function handleDistributeAll() {
    if (selectedFilesAll.length === 0) {
      toast({
        variant: "destructive",
        description: "Selecione pelo menos um arquivo para confirmar a distribuição.",
      });
      return;
    }

    if (!distributeAllSnapshot || Object.keys(distributeAllSnapshot).length === 0) {
      toast({
        variant: "destructive",
        description: "Nenhum produto para distribuir.",
      });
      return;
    }

    setLoadingButton(true);
    setIsDistributingAll(true);
    setShowDistributeAllDialog(false);

    let successCount = 0;
    let errorCount = 0;
    let skippedCount = 0;

    // Usar o snapshot capturado quando o dialog foi aberto
    const allProductIds = Object.keys(distributeAllSnapshot);

    for (const productIdStr of allProductIds) {
      try {
        const productId = parseInt(productIdStr);
        const unitiesToDistribute = distributeAllSnapshot[productId] || [];

        if (unitiesToDistribute.length === 0) {
          skippedCount++;
          continue;
        }

        const dataToSubmit: SchemaType = {
          contest_id: contestData.id,
          lottery_id: contestData.lottery?.id,
          contest_product_classification_id: productId,
          unities: unitiesToDistribute.map((uq) => ({
            id: uq.unityId,
            quota_quantity: uq.quota,
          })),
        };

        const parseResult = schema.safeParse(dataToSubmit);
        if (!parseResult.success) {
          errorCount++;
          continue;
        }

        // Só valida limite se o produto está na página atual (simplifiedData)
        const productInCurrentPage = simplifiedData.find(
          (p) => p.contest_product_classification.id === productId
        );
        if (productInCurrentPage) {
          const validationInfo = getQuotaValidationInfo(productId, unitiesToDistribute);
          if (validationInfo.isOverLimit) {
            errorCount++;
            continue;
          }
        }

        const formData = new FormData();
        formData.append("contest_id", dataToSubmit.contest_id.toString());
        formData.append("lottery_id", dataToSubmit.lottery_id.toString());
        formData.append("contest_product_classification_id", dataToSubmit.contest_product_classification_id.toString());

        dataToSubmit.unities.forEach((unity, index) => {
          formData.append(`unities[${index}][id]`, unity.id.toString());
          formData.append(`unities[${index}][quota_quantity]`, unity.quota_quantity.toString());
        });

        selectedFilesAll.forEach((file) => {
          formData.append("files[]", file);
        });

        const { ok } = await storeDistributionsDataUnityBalancesWithFiles(
          String(contestData.id),
          formData
        );

        if (ok) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        errorCount++;
      }
    }

    try {
      setLoadingButton(false);
      // Limpar todos os states após distribuir (para todas as páginas)
      setQuotaStates({});
      setOriginalQuotaStates({});
      setProductNamesCache({});
      setDistributeAllSnapshot(null);
      userChangesRef.current = {}; // Limpar o ref também
      // Recarregar dados simplificados após salvar usando a página atual
      await fetchPage(currentPage);
    } catch (error) {
      console.error("Erro ao atualizar distribuições:", error);
    }

    setIsDistributingAll(false);
    setSelectedFilesAll([]);
    setDistributeAllSnapshot(null);

    if (successCount > 0 && errorCount === 0) {
      const message =
        skippedCount > 0
          ? `${successCount} distribuições salvas com sucesso! ${skippedCount} produtos sem cotas foram ignorados.`
          : `Todas as ${successCount} distribuições foram salvas com sucesso!`;
      toast({
        description: message,
        action: <Check color="#1edc2b" />,
      });
    } else if (successCount > 0 && errorCount > 0) {
      const message =
        skippedCount > 0
          ? `${successCount} distribuições salvas, ${errorCount} falharam, ${skippedCount} ignorados.`
          : `${successCount} distribuições salvas com sucesso, ${errorCount} falharam.`;
      toast({
        description: message,
        action: <Check color="#ff8000" />,
      });
    } else if (skippedCount > 0 && successCount === 0 && errorCount === 0) {
      toast({
        description:
          "Nenhuma distribuição processada. Defina cotas para os produtos primeiro.",
      });
    } else {
      toast({
        variant: "destructive",
        description: "Erro ao salvar todas as distribuições",
      });
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-40">
        <LoaderSpin />
      </div>
    );
  }

  return (
    <>
      <div className="h-[70vh] flex flex-col" style={{ maxHeight: "70vh" }}>
        {/* Header fixo */}
        <div className="flex-shrink-0">
          {/* Header melhorado do concurso */}
          <div className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 rounded-lg p-4 mb-4 border border-slate-200 dark:border-slate-700">
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div
                    className="px-3 py-1.5 rounded-lg text-white text-sm font-semibold shadow-sm"
                    style={{ backgroundColor: contestData?.lottery?.color || '#023859' }}
                  >
                    {contestData?.lottery?.name}
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                      {contestData?.name}
                    </h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Validade: {contestData?.validation_date
                        ? Mask.transformMaskBirthdayInBr(contestData?.validation_date)
                        : "Não informado"}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={handleOpenDistributeAllDialog}
                  className={cn("flex gap-2", colors.bg_primary)}
                  disabled={isDistributingAll || isLoading}
                >
                  {isDistributingAll ? (
                    <>
                      Distribuindo... <LoaderSpin />
                    </>
                  ) : (
                    "Distribuir Tudo"
                  )}
                </Button>
              </div>
              <div className="border-t border-slate-200 dark:border-slate-600 pt-3 flex justify-end gap-2">
                <Button
                  size="sm"
                  onClick={handleExportTemplate}
                  className="flex gap-2 bg-green-600 hover:bg-green-700 text-white"
                  disabled={isExporting || isLoading}
                >
                  {isExporting ? (
                    <>
                      <LoaderSpin /> Exportando...
                    </>
                  ) : (
                    <>
                      <FileSpreadsheet className="w-4 h-4" />
                      Exportar Modelo
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  onClick={() => setShowImportDialog(true)}
                  className="flex gap-2 bg-green-600 hover:bg-green-700 text-white"
                  disabled={isLoading}
                >
                  <Upload className="w-4 h-4" />
                  Importar Excel
                </Button>
              </div>
            </div>
          </div>

          {/* Filtro por B */}
          <div className="flex items-center gap-2 mb-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">B:</span>
            <Input
              type="text"
              value={productFilter}
              onChange={(e) => handleFilterChange(e.target.value)}
              onKeyPress={handleFilterKeyPress}
              placeholder="Ex: 200"
              className="w-24 h-9"
              maxLength={5}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleFilterSearch}
              className="h-9"
              disabled={isLoading}
            >
              Buscar
            </Button>
            {productFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearFilter}
                className="h-9 text-slate-500"
                disabled={isLoading}
              >
                Limpar
              </Button>
            )}
            {paginationInfo && (
              <span className="ml-auto text-sm text-slate-500 dark:text-slate-400">
                {paginationInfo.total} produto{paginationInfo.total !== 1 ? 's' : ''} encontrado{paginationInfo.total !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* Paginação superior */}
          {paginationInfo && paginationInfo.last_page > 1 && (
            <div className="flex justify-between items-center mb-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
              <span className="text-sm text-slate-600 dark:text-slate-400">
                Exibindo {paginationInfo.from} - {paginationInfo.to} de {paginationInfo.total} produtos
              </span>
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      className={cn("h-8", colors.bg_primary, currentPage === 1 && "opacity-50 pointer-events-none")}
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(currentPage - 1);
                      }}
                    />
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationLink
                      href="#"
                      className="size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent"
                    >
                      <span className="pointer-events-none dark:text-[#F4F4F5]">
                        {currentPage}
                      </span>
                    </PaginationLink>
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationLink
                      href="#"
                      className="text-black h-8 hover:bg-stone-950/10"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(paginationInfo.last_page);
                      }}
                    >
                      <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                    </PaginationLink>
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      className={cn("h-8", colors.bg_primary, currentPage === paginationInfo.last_page && "opacity-50 pointer-events-none")}
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(currentPage + 1);
                      }}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </div>

        {/* Área com scroll - apenas a tabela */}
        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col gap-3">
            <div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Produto</TableHead>
                    <TableHead>Qtd. Cotas</TableHead>
                    <TableHead>Preço de venda</TableHead>
                    <TableHead className="text-center">Ações</TableHead>
                    <TableHead className="text-center">Distribuição</TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody striped>
                  {simplifiedData?.map((productData) => {
                    const product = {
                      id: productData.contest_product_classification.id,
                      product_name: productData.contest_product_classification.product_name,
                      product_classification: {
                        name: "",
                        quota_quantity: productData.contest_product_classification.quota_total_quantity,
                        sale_price: productData.contest_product_classification.sale_price,
                      },
                      lottery_id: contestData.lottery?.id,
                    };
                    const localQuotas = quotaStates[product.id] || [];

                    return (
                      <TableRow key={product.id} className="transition-colors">
                        <TableCell className="align-top">
                          {productData.contest_product_classification.product_name}
                        </TableCell>
                        <TableCell className="text-center align-top">
                          {(() => {
                            const totalQuotas =
                              productData.contest_product_classification
                                .quota_total_quantity;
                            const totalDistributed =
                              productData.contest_product_classification
                                .quota_total_distributed;

                            return (
                              <div className="flex flex-col items-center gap-1">
                                <span>{`${totalDistributed} / ${totalQuotas}`}</span>
                                {(() => {
                                  const available = getAvailableBalance(
                                    product.id
                                  );
                                  return (
                                    <>
                                      {available > 0 && (
                                        <span className="text-xs text-red-500 dark:text-red-400 font-medium">
                                          {available} restantes para
                                          distribuição
                                        </span>
                                      )}
                                      {available === 0 &&
                                        totalDistributed < totalQuotas && (
                                          <span className="text-xs text-red-600 dark:text-red-400 font-medium">
                                            Sem saldo disponível
                                          </span>
                                        )}
                                      {totalDistributed === totalQuotas && (
                                        <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                                          Todas as cotas distribuídas
                                        </span>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="text-center align-top">
                          {Mask.formatCurrency(
                            productData.contest_product_classification.sale_price || 0
                          )}
                        </TableCell>
                        <TableCell className="text-center align-top">
                          <Button
                            onClick={() =>
                              handleShowDetails(
                                product.id,
                                productData.contest_product_classification.product_name
                              )
                            }
                            title="Ver detalhes das distribuições"
                            variant="ghost"
                            size="icon"
                            className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="border rounded p-2 mb-2 max-h-40 overflow-auto">
                            {productData.unities.map((unity) => {
                              const quotaValue =
                                localQuotas.find(
                                  (q) => q.unityId === unity.id
                                )?.quota ?? 0;

                              const distributedQuota = getDistributedQuota(
                                product.id,
                                unity.id
                              );

                              return (
                                <div
                                  key={unity.id}
                                  className="flex items-center gap-2 mb-2 border-b border-gray-200 dark:border-gray-700 pb-2"
                                >
                                  <div className="w-40 text-sm">
                                    <span className="font-medium">
                                      {unity.name}
                                    </span>
                                    {distributedQuota > 0 && (
                                      <span className="text-xs text-blue-600 dark:text-blue-400 ml-2">
                                        ({distributedQuota} distribuída
                                        {distributedQuota > 1 ? "s" : ""})
                                      </span>
                                    )}

                                    {/* Mostrar saldo real da unidade para este produto */}
                                    <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                                      Cotas disponíveis:{" "}
                                      {unity.contest_product_balance.current_balance}
                                    </div>
                                  </div>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={quotaValue}
                                    onChange={(e) =>
                                      handleQuotaChange(
                                        product.id,
                                        unity.id,
                                        parseInt(e.target.value) || 0
                                      )
                                    }
                                    className="w-20"
                                    disabled={distributingProductId === product.id}
                                  />
                                </div>
                              );
                            })}
                          </div>

                          {(() => {
                            const validationInfo = getQuotaValidationInfo(
                              product.id,
                              localQuotas
                            );
                            if (validationInfo.totalInputQuotas === 0)
                              return null;

                            return (
                              <div className="mt-2 mb-2 p-2 rounded bg-gray-50 dark:bg-gray-800">
                                <div className="text-sm">
                                  <span className="font-medium">
                                    Total: {validationInfo.totalInputQuotas} /{" "}
                                    {validationInfo.availableBalance}
                                  </span>
                                  {validationInfo.isOverLimit ? (
                                    <div className="text-red-600 dark:text-red-400 text-xs mt-1">
                                      ⚠️ Excesso de{" "}
                                      {validationInfo.totalInputQuotas -
                                        validationInfo.availableBalance}{" "}
                                      cotas sobre o limite disponível
                                      {validationInfo.withHeldBalance > 0 && (
                                        <span className="block">
                                          ({validationInfo.withHeldBalance} em
                                          processamento)
                                        </span>
                                      )}
                                    </div>
                                  ) : validationInfo.remaining > 0 ? (
                                    <div className="text-orange-600 dark:text-orange-400 text-xs mt-1">
                                      💡 Ainda restam {validationInfo.remaining}{" "}
                                      cotas disponíveis para distribuir
                                    </div>
                                  ) : (
                                    <div className="text-green-600 dark:text-green-400 text-xs mt-1">
                                      ✅ Utilizando todo o saldo disponível
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })()}

                          <div className="flex flex-col gap-2 mb-4">
                            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Ações de Distribuição
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleCopyDistribution(product.id)}
                              className="flex items-center gap-2 justify-start"
                            >
                              <Copy className="w-4 h-4" />
                              Copiar Distribuição Feita
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                handleCopyCurrentDistribution(product.id)
                              }
                              className="flex items-center gap-2 justify-start"
                              disabled={distributingProductId === product.id}
                            >
                              <Copy className="w-4 h-4" />
                              Copiar Dados Atuais
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                handlePasteDistribution(product.id)
                              }
                              disabled={!copiedDistribution}
                              className="flex items-center gap-2 justify-start"
                            >
                              <Clipboard className="w-4 h-4" />
                              Colar Distribuição
                            </Button>
                          </div>

                          <Button
                            variant="outline"
                            className={cn(
                              "w-full",
                              (() => {
                                const validationInfo = getQuotaValidationInfo(
                                  product.id,
                                  localQuotas
                                );
                                return validationInfo.isOverLimit
                                  ? "bg-red-100 border-red-300 text-red-700 hover:bg-red-200 dark:bg-red-900 dark:border-red-700 dark:text-red-300"
                                  : colors.bg_primary;
                              })()
                            )}
                            disabled={
                              distributingProductId === product.id ||
                              (() => {
                                const validationInfo = getQuotaValidationInfo(
                                  product.id,
                                  localQuotas
                                );
                                const hasValidChanges = localQuotas.some(quota => quota.quota > 0);
                                return validationInfo.isOverLimit || !hasValidChanges;
                              })()
                            }
                            onClick={() => setConfirmDialogProduct(product)}
                          >
                            {distributingProductId === product.id ? (
                              <LoaderSpin />
                            ) : (
                              (() => {
                                const validationInfo = getQuotaValidationInfo(
                                  product.id,
                                  localQuotas
                                );
                                const hasValidChanges = localQuotas.some(quota => quota.quota > 0);
                                
                                if (validationInfo.isOverLimit) {
                                  return "Distribuição bloqueada (excede limite)";
                                }
                                if (!hasValidChanges) {
                                  return "Defina cotas para distribuir";
                                }
                                return "Fazer distribuição";
                              })()
                            )}
                          </Button>

                          <Dialog
                            open={
                              confirmDialogProduct &&
                              confirmDialogProduct.id === product.id
                            }
                            onOpenChange={(open) =>
                              !open && setConfirmDialogProduct(null)
                            }
                          >
                            <DialogContent
                              className="z-[60]"
                              style={{ zIndex: 60 }}
                            >
                              <DialogHeader>
                                <DialogTitle>
                                  Confirmar Distribuição
                                </DialogTitle>
                                <DialogDescription>
                                  {confirmDialogProduct &&
                                    (() => {
                                      const confirmLocalQuotas =
                                        quotaStates[confirmDialogProduct.id] ||
                                        [];
                                      const confirmTotalQuotas =
                                        confirmLocalQuotas.reduce(
                                          (sum, item) => sum + item.quota,
                                          0
                                        );
                                      return (
                                        <div className="space-y-2">
                                          <p>
                                            Você está prestes a distribuir as
                                            cotas para o produto:
                                          </p>
                                          <p className="font-semibold">
                                            {confirmDialogProduct.product_name}{" "}
                                            -{" "}
                                            {
                                              confirmDialogProduct
                                                .product_classification.name
                                            }
                                          </p>
                                          <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded">
                                            <p className="text-sm mb-2">
                                              <strong>
                                                Distribuição por unidade:
                                              </strong>
                                            </p>
                                            <div className="space-y-1 mb-3">
                                              {confirmLocalQuotas.map(
                                                (quota) => {
                                                  const confirmProductData =
                                                    simplifiedData.find(
                                                      (p) =>
                                                        p
                                                          .contest_product_classification
                                                          .id === confirmDialogProduct.id
                                                    );
                                                  const unity =
                                                    confirmProductData?.unities.find(
                                                      (u) =>
                                                        u.id === quota.unityId
                                                    );
                                                  const quotaValue =
                                                    quota.quota;
                                                  if (quotaValue > 0) {
                                                    return (
                                                      <div
                                                        key={quota.unityId}
                                                        className="text-sm"
                                                      >
                                                        <span className="font-medium">
                                                          {unity?.name}
                                                        </span>{" "}
                                                        -{" "}
                                                        <span className="text-blue-600 dark:text-blue-400">
                                                          {quotaValue} cotas
                                                        </span>
                                                      </div>
                                                    );
                                                  }
                                                  return null;
                                                }
                                              )}
                                            </div>
                                            <div className="border-t pt-2">
                                              <p className="text-sm">
                                                <strong>
                                                  Total de cotas a distribuir:
                                                </strong>{" "}
                                                {confirmTotalQuotas}
                                              </p>
                                              <p className="text-sm">
                                                <strong>
                                                  Máximo disponível:
                                                </strong>{" "}
                                                {getAvailableBalance(
                                                  confirmDialogProduct.id
                                                )}
                                              </p>
                                              {getAvailableBalance(
                                                confirmDialogProduct.id
                                              ) > confirmTotalQuotas && (
                                                <p className="text-sm text-orange-600 dark:text-orange-400">
                                                  <strong>Restam:</strong>{" "}
                                                  {getAvailableBalance(
                                                    confirmDialogProduct.id
                                                  ) - confirmTotalQuotas}{" "}
                                                  cotas para distribuir
                                                </p>
                                              )}
                                            </div>
                                          </div>
                                          <FileUpload
                                            files={selectedFiles}
                                            onFilesChange={setSelectedFiles}
                                            inputId="file-upload-distribution-single"
                                          />
                                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">
                                            Esta ação não pode ser desfeita.
                                            Deseja continuar?
                                          </p>
                                        </div>
                                      );
                                    })()}
                                </DialogDescription>
                              </DialogHeader>
                              <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
                                <Button
                                  variant="outline"
                                  onClick={() => {
                                    setConfirmDialogProduct(null);
                                    setSelectedFiles([]);
                                  }}
                                >
                                  Cancelar
                                </Button>
                                <Button
                                  onClick={() => {
                                    if (confirmDialogProduct) {
                                      handleSubmitDistribution(
                                        confirmDialogProduct
                                      );
                                      setConfirmDialogProduct(null);
                                    }
                                  }}
                                  className={cn(colors.bg_primary)}
                                  disabled={selectedFiles.length === 0}
                                >
                                  Confirmar Distribuição
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {simplifiedData?.length < 1 && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        Nenhum produto encontrado.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Paginação inferior */}
          {paginationInfo && paginationInfo.last_page > 1 && (
            <div className="flex justify-between items-center mt-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
              <span className="text-sm text-slate-600 dark:text-slate-400">
                Página {paginationInfo.current_page} de {paginationInfo.last_page}
              </span>
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      className={cn("h-8", colors.bg_primary, currentPage === 1 && "opacity-50 pointer-events-none")}
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(currentPage - 1);
                      }}
                    />
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationLink
                      href="#"
                      className="size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent"
                    >
                      <span className="pointer-events-none dark:text-[#F4F4F5]">
                        {currentPage}
                      </span>
                    </PaginationLink>
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationLink
                      href="#"
                      className="text-black h-8 hover:bg-stone-950/10"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(paginationInfo.last_page);
                      }}
                    >
                      <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                    </PaginationLink>
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      className={cn("h-8", colors.bg_primary, currentPage === paginationInfo.last_page && "opacity-50 pointer-events-none")}
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(currentPage + 1);
                      }}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </div>

        <Dialog
          open={showDistributeAllDialog}
          onOpenChange={setShowDistributeAllDialog}
        >
          <DialogContent className="z-[60]" style={{ zIndex: 60 }}>
            <DialogHeader>
              <DialogTitle>Confirmar Distribuição Geral</DialogTitle>
              <DialogDescription>
                <div className="space-y-2">
                  <p>
                    Você está prestes a distribuir as cotas para{" "}
                    <strong>todos os produtos</strong> do concurso:
                  </p>
                  <p className="font-semibold">
                    {contestData?.name} - {contestData?.lottery?.name}
                  </p>
                  <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded">
                    <p className="text-sm mb-2">
                      <strong>Produtos que serão distribuídos (de todas as páginas):</strong>
                    </p>
                    <div
                      className="h-40 w-full overflow-y-auto border rounded p-2"
                      style={{ maxHeight: "160px" }}
                    >
                      <div className="space-y-1">
                        {(() => {
                          // Usar o snapshot capturado quando o dialog foi aberto
                          if (!distributeAllSnapshot || Object.keys(distributeAllSnapshot).length === 0) {
                            return (
                              <div className="text-sm text-gray-500 p-2">
                                Nenhum produto com cotas definidas.
                              </div>
                            );
                          }

                          const productsWithChanges = Object.entries(distributeAllSnapshot)
                            .map(([productIdStr, localQuotas]) => {
                              const productId = parseInt(productIdStr);
                              const productData = simplifiedData.find(
                                (p) => p.contest_product_classification.id === productId
                              );
                              const totalQuotas = localQuotas.reduce((sum, item) => sum + item.quota, 0);
                              return { productId, productData, localQuotas, totalQuotas };
                            });

                          return productsWithChanges.map(({ productId, productData, localQuotas, totalQuotas }) => {
                            // Usa o cache para nomes de produtos/unidades de outras páginas
                            const cachedData = productNamesCache[productId];
                            const productName = productData?.contest_product_classification.product_name
                              || cachedData?.productName
                              || `Produto #${productId}`;

                            return (
                              <div
                                key={productId}
                                className="text-sm p-3 rounded border bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800"
                              >
                                <div className="font-medium mb-2">
                                  {productName}
                                  {!productData && <span className="text-xs text-gray-500 ml-2">(outra página)</span>}
                                </div>

                                <div className="space-y-1 mb-2">
                                  <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                    Distribuição por unidade:
                                  </p>
                                  {localQuotas.map((quota) => {
                                    // Primeiro tenta da página atual, depois do cache
                                    const unity = productData?.unities.find((u) => u.id === quota.unityId);
                                    const unityName = unity?.name || cachedData?.unities?.[quota.unityId] || `Unidade #${quota.unityId}`;
                                    if (quota.quota > 0) {
                                      return (
                                        <div key={quota.unityId} className="text-xs">
                                          <span className="font-medium">
                                            {unityName}
                                          </span>{" "}
                                          -{" "}
                                          <span className="text-blue-600 dark:text-blue-400">
                                            {quota.quota} cotas
                                          </span>
                                        </div>
                                      );
                                    }
                                    return null;
                                  })}
                                </div>

                                <div className="border-t pt-2 space-y-1">
                                  <div className="text-xs">
                                    <strong>Total de cotas a distribuir:</strong> {totalQuotas}
                                  </div>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  </div>

                  <FileUpload
                    files={selectedFilesAll}
                    onFilesChange={setSelectedFilesAll}
                    inputId="file-upload-distribution-all"
                  />

                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">
                    Esta ação irá processar todos os produtos com cotas
                    definidas. Produtos sem cotas serão ignorados. Deseja
                    continuar?
                  </p>
                </div>
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDistributeAllDialog(false);
                  setSelectedFilesAll([]);
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleDistributeAll}
                className={cn(colors.bg_primary)}
                disabled={loadingButton || selectedFilesAll.length === 0}
              >
                {
                  loadingButton ? (
                    <>
                      <LoaderSpin />
                      Distribuindo...
                    </>
                  ) : (
                    "Confirmar Distribuição Geral"
                  )
                }
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      
      {selectedProductDetails && (
        <DistributionDetailsModal
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedProductDetails(null);
          }}
          contestId={String(contestData.id)}
          productId={selectedProductDetails.id}
          productName={selectedProductDetails.name}
        />
      )}

      {/* Modal de Importação */}
      <Dialog open={showImportDialog} onOpenChange={handleCloseImportDialog}>
        <DialogContent className="w-[95vw] max-w-4xl max-h-[90vh] overflow-hidden flex flex-col z-[60]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Importar Distribuição do Excel
            </DialogTitle>
            <DialogDescription>
              {!importPreview
                ? "Selecione o arquivo Excel preenchido com as distribuições."
                : "Confira o preview da distribuição antes de confirmar."
              }
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto">
            {!importPreview ? (
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                  <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">
                    Instruções:
                  </h4>
                  <ol className="text-sm text-blue-700 dark:text-blue-300 list-decimal list-inside space-y-1">
                    <li>Clique em "Exportar Modelo" para baixar a planilha</li>
                    <li>Preencha as quantidades nas colunas das unidades</li>
                    <li>Salve o arquivo e faça o upload aqui</li>
                    <li>Confira o preview e confirme a distribuição</li>
                  </ol>
                </div>

                <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  importFile
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                    : 'border-gray-300 dark:border-gray-600'
                }`}>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                    className="hidden"
                    id="import-file-input"
                  />
                  <label
                    htmlFor="import-file-input"
                    className="cursor-pointer flex flex-col items-center gap-2"
                  >
                    <Upload className={`w-10 h-10 ${importFile ? 'text-green-500' : 'text-gray-400'}`} />
                    <span className={`text-sm ${importFile ? 'text-green-600 dark:text-green-400 font-medium' : 'text-gray-600 dark:text-gray-400'}`}>
                      {importFile ? importFile.name : "Clique para selecionar o arquivo Excel"}
                    </span>
                  </label>
                </div>

                {importFile && (
                  <Button
                    onClick={handleImportPreview}
                    className={cn("w-full", colors.bg_primary)}
                    disabled={isImporting}
                  >
                    {isImporting ? (
                      <>
                        <LoaderSpin /> Processando...
                      </>
                    ) : (
                      "Processar Arquivo"
                    )}
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {/* Resumo */}
                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  <div className="p-2 sm:p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                    <p className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400">
                      {importPreview.summary?.total_products || 0}
                    </p>
                    <p className="text-[10px] sm:text-xs text-green-700 dark:text-green-300">Produtos</p>
                  </div>
                  <div className="p-2 sm:p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                    <p className="text-xl sm:text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {importPreview.summary?.total_quotas || 0}
                    </p>
                    <p className="text-[10px] sm:text-xs text-blue-700 dark:text-blue-300">Total Cotas</p>
                  </div>
                  <div className="p-2 sm:p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-center">
                    <p className="text-xl sm:text-2xl font-bold text-red-600 dark:text-red-400">
                      {importPreview.summary?.total_errors || 0}
                    </p>
                    <p className="text-[10px] sm:text-xs text-red-700 dark:text-red-300">Erros</p>
                  </div>
                </div>

                {/* Erros */}
                {importPreview.errors?.length > 0 && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-700">
                    <h4 className="font-medium text-red-800 dark:text-red-200 mb-2">
                      Erros encontrados:
                    </h4>
                    <ul className="text-sm text-red-700 dark:text-red-300 list-disc list-inside space-y-1 max-h-24 overflow-y-auto">
                      {importPreview.errors.map((error: string, index: number) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Lista de distribuições */}
                {importPreview.distributions?.length > 0 && (
                  <div className="border rounded-lg max-h-60 overflow-auto">
                    <table className="w-full text-sm min-w-[500px]">
                      <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                        <tr>
                          <th className="px-2 sm:px-3 py-2 text-left whitespace-nowrap">Produto</th>
                          <th className="px-2 sm:px-3 py-2 text-center whitespace-nowrap">Disponível</th>
                          <th className="px-2 sm:px-3 py-2 text-center whitespace-nowrap">A Distribuir</th>
                          <th className="px-2 sm:px-3 py-2 text-left">Unidades</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importPreview.distributions.map((dist: any, index: number) => (
                          <tr key={index} className="border-t">
                            <td className="px-2 sm:px-3 py-2 font-medium whitespace-nowrap">{dist.product_name}</td>
                            <td className="px-2 sm:px-3 py-2 text-center">{dist.available}</td>
                            <td className="px-2 sm:px-3 py-2 text-center text-blue-600 dark:text-blue-400 font-medium">
                              {dist.total_distributed}
                            </td>
                            <td className="px-2 sm:px-3 py-2 text-xs break-words">
                              {dist.unities.map((u: any) => `${u.name}: ${u.quota_quantity}`).join(", ")}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Upload de arquivos de comprovação */}
                {importPreview.distributions?.length > 0 && (
                  <div className="space-y-2">
                    <FileUpload
                      files={importConfirmFiles}
                      onFilesChange={setImportConfirmFiles}
                      inputId="import-confirm-files"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={handleCloseImportDialog}>
              Cancelar
            </Button>
            {importPreview && importPreview.distributions?.length > 0 && (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    setImportPreview(null);
                    setImportConfirmFiles([]);
                  }}
                >
                  Voltar
                </Button>
                <Button
                  onClick={handleConfirmImport}
                  className={cn(colors.bg_primary)}
                  disabled={isConfirmingImport || importConfirmFiles.length === 0}
                >
                  {isConfirmingImport ? (
                    <>
                      <LoaderSpin /> Confirmando...
                    </>
                  ) : (
                    "Confirmar Distribuição"
                  )}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
